import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { catchError, Subscription, throwError } from 'rxjs';
import { ChartComponent } from 'ng-apexcharts';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { OurDate } from 'src/app/core/interfaces/general/our-date';
import { populateOurDate } from 'src/app/core/functions-generic/functions-generic-date';
import { TippyToolTipService } from 'src/app/core/services/utelize/general/tippy-tool-tip-service';
import { WidgetCompanyTotalCostDTO } from 'src/app/core/models/system/widget/widget-company-total-cost.dto';
import { WidgetIccidTotalCostDTO } from 'src/app/core/models/system/widget/widget-Iccid-total-cost.dto';
import { WidgetService } from 'src/app/core/services/utelize/general/widget-service';

@Component({
  selector: 'app-chart-costs-per-day',
  templateUrl: './chart-costs-per-day.component.html',
  styleUrls: ['./chart-costs-per-day.component.scss'],
  providers: [TippyToolTipService]
})

export class ChartCostsPerDayComponent implements AfterViewInit, OnDestroy, OnInit {
  
  //CRN_20251210 - Use this either at Company Level or SIM Level
  
  @Input() allowContainerClose: boolean | undefined;
  @Input() chartHeight        : number  | undefined;
  @Input() chartTitle         : string  | undefined;
  @Input() containerGrow      : boolean | undefined;
  @Input() evenDaysOnly       : boolean | undefined;
  @Input() marginBottom       : string  | undefined="mb-0";
  @Input() showTitleHintText  : boolean | undefined;
  
  @Output() containerClosed         : EventEmitter<void>=new EventEmitter<void>();
  @Output() eSimManagerFormCancelled: EventEmitter<void>=new EventEmitter<void>();
  
  @ViewChild('chartRef') chartRef!: ChartComponent;
 
  private buildIccidTotalCostChart   : boolean=true;
  private filterCompanyID            : number       | undefined;
  private filterDate                 : OurDate      | undefined=populateOurDate(this.exceptionsService, Date.now());
  private iccid                      : string       | undefined;
  private subIccidTotalCost!         : Subscription | undefined;
  private widgetIccidCostAccumulative: number[]=[];
  private widgetIccidCostDayOfMonth  : string[]=[];
  private widgetIccidCostSpendLimit  : number[]=[];
  private widgetIccidCostTotal       : number[]=[];
  
  public expandContainer : boolean=true;
  public iccidChartCosts : any    | undefined;
  public noData          : boolean=false;
  public visibleContainer: boolean=true;

  constructor(
    private exceptionsService  : ExceptionsService,
    private tippyToolTipService: TippyToolTipService,
    private widgetService      : WidgetService
  ) {

  }

  public ngOnInit(): void {
    try {
      if (this.chartHeight==undefined)
        this.chartHeight=250;

      if (this.chartTitle==undefined)
        this.chartTitle="Costs Per Day";
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public ngAfterViewInit(): void {
    try {
      this.buildIccidTotalCostChart=true;
      
      this.tippyToolTipService.setupBootstrapTooltips('[data-bs-toggle="tooltip"]', 'left');
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public ngOnDestroy(): void {
    try {
      this.unsubscribeIccidTotalCost();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private getChartColorsArray(colors: any) {
    try {
      colors=JSON.parse(colors);

      return colors.map(function (value:any) {
        var newValue=value.replace(" ", "");
        
        if (newValue.indexOf(",")===-1) {
          var color=getComputedStyle(document.documentElement).getPropertyValue(newValue);
          
          if (color) {
            color=color.replace(" ", "");
            
            return color;

          } else {
            return newValue;
          }
        } else {
          var val=value.split(',');
          
          if (val.length==2) {
            var rgbaColor=getComputedStyle(document.documentElement).getPropertyValue(val[0]);
            
            rgbaColor="rgba(" + rgbaColor + "," + val[1] + ")";
            
            return rgbaColor;

          } else {
            return newValue;
          }
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getCompanyTotalCost(filterCompanyID: number | undefined, filterDate: OurDate | undefined) {
    try {
      if (filterCompanyID==undefined)
        throw new Error("ChartCostsPerDayComponent - CompanyID is undefined");

      this.filterCompanyID=filterCompanyID;
      this.filterDate     =filterDate;

      this.getTotalCost();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getIccidTotalCost(iccid: string | undefined) {
    try {
      if (iccid==undefined)
        throw new Error("ChartCostsPerDayComponent - ICCID is undefined");

      this.iccid=iccid;

      this.getTotalCost();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public getTotalCost() {
    try {
      this.unsubscribeIccidTotalCost();
      this.subIccidTotalCost=new Subscription();

      const companyID  =<number>(this.filterCompanyID   ?? 0);
      const iccid      =this.iccid                      ?? "";
      const year       =<number>(this.filterDate?.year  ?? 1);
      const month      =<number>(this.filterDate?.month ?? 1);
      const day        =<number>(this.filterDate?.day   ?? 1);
      //CRN_20261013 - Retired
      // const sourceUsage=this.iccid
      //                   ? this.widgetService.getIccidTotalCost  (iccid,     year, month, day)
      //                   : this.widgetService.getCompanyTotalCost(companyID, year, month, day);
      //
      // const sourceUsageSubscription=sourceUsage
      // .pipe(
      //   catchError(error =>
      //     throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
      //   )
      // )
      // .subscribe({
      //   next : data => this.updateIccidCostChart(data),
      //   error: error => this.exceptionsService.handleException(new Error(this.exceptionsService.formatHttpError(error)))
      // });
      //
      // this.subIccidTotalCost.add(sourceUsageSubscription);

      const sourceUsageUpdates=this.iccid
                               ? this.widgetService.getIccidTotalCostUpdates  (iccid,     year, month, day)
                               : this.widgetService.getCompanyTotalCostUpdates(companyID, year, month, day);

      const sourceUsageUpdatesSubscription=sourceUsageUpdates
      .pipe(
        catchError(error =>
          throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        )
      )
      .subscribe({
        next : data => this.updateIccidCostChart(data),
        error: error => this.exceptionsService.handleException(new Error(error))
      });

      this.subIccidTotalCost.add(sourceUsageUpdatesSubscription);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  private updateIccidCostChart(data: WidgetCompanyTotalCostDTO[] | WidgetIccidTotalCostDTO[]) {
    try {
      this.widgetIccidCostAccumulative=[];
      this.widgetIccidCostDayOfMonth  =[];
      this.widgetIccidCostSpendLimit  =[];
      this.widgetIccidCostTotal       =[];

      this.noData=(data ?? []).length===0;

      if (this.noData) {
        this.buildIccidTotalCostChart=true;
        this.iccidChartCosts         =undefined;
        return;
      }

      for (let d of data) {
        this.widgetIccidCostAccumulative.push(d.accumulativeCost);
        this.widgetIccidCostDayOfMonth  .push(!this.evenDaysOnly || d.dayOfMonth%2==0 ? (<string><unknown>d.dayOfMonth) : "");

        if (this.iccid && "spendLimit" in d)
          this.widgetIccidCostSpendLimit.push(d.spendLimit);

        this.widgetIccidCostTotal.push(d.totalCost);
      }

      if (this.buildIccidTotalCostChart) {
        this.buildIccidTotalCostChart=false;
        
        //this.iccidTotalCostForCurrentMonthChart('["--vz-primary", "--vz-success", "--vz-danger"]');
        this.iccidTotalCostForCurrentMonthChart('["--vz-suresim-blue", "--vz-success", "--vz-danger"]');
      }

      if (this.chartRef?.updateSeries) {
        const series=[
          {
            name: 'Total Cost £',
            data: this.widgetIccidCostTotal,
            type: 'column'
          },
          {
            name: 'Accumulative Cost £',
            data: this.widgetIccidCostAccumulative,
            type: 'line'
          }
        ];

        if (this.iccid && this.widgetIccidCostSpendLimit.length>0)
          series.push({
            name: 'Spend Limit £',
            data: this.widgetIccidCostSpendLimit,
            type: 'line'
          });

        this.chartRef.updateSeries(series, true);

        this.chartRef.updateOptions({
          chart: {
            zoom: { enabled: false },
            pan : { enabled: false }
          },
          labels     : this.widgetIccidCostDayOfMonth,
          plotOptions: this.buildPlotOptions(this.widgetIccidCostDayOfMonth.length)
        }, false);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public unsubscribeIccidTotalCost() {
    try {
      if (this.subIccidTotalCost) {
        this.subIccidTotalCost.unsubscribe();
        this.subIccidTotalCost=undefined;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private iccidTotalCostForCurrentMonthChart(colors: any) {
    try {
            colors       =this.getChartColorsArray(colors);
      const hasSpendLimit=this.iccid && this.widgetIccidCostSpendLimit.length>0;
      const series       =[
        {
          name: 'Total Cost £',
          data: this.widgetIccidCostTotal,
          type: 'column'
        },
        {
          name: 'Accumulative Cost £',
          data: this.widgetIccidCostAccumulative,
          type: 'line'
        }
      ];

      if (hasSpendLimit)
        series.push({
          name: 'Spend Limit £',
          data: this.widgetIccidCostSpendLimit,
          type: 'line'
        });

      const strokeWidths=hasSpendLimit ? [0, 3, 3] : [0, 3];
      const yaxis: any[]=[
        {
          title: {
            style: {
              fontWeight: 600,
            },
            text: 'Total Cost'
          },
        }
      ];

      if (hasSpendLimit)
        yaxis.push({
          min: 0,
          opposite: true,
          title: {
            style: {
              fontWeight: 600,
            },
            text: 'Spend Limit'
          }
        });

      this.iccidChartCosts={
        chart: {
          height: this.chartHeight,
          pan   : {
            enabled: false
          },
          toolbar: {
              show: false,
          },
          type: 'line',
          zoom: {
            enabled: false
          },
        },
        colors: colors,
        // dataLabels: {
        //   enabled        : true,
        //   enabledOnSeries: [1]
        // },
        labels: this.widgetIccidCostDayOfMonth,
        // plotOptions: {
        //   bar: {
        //     columnWidth: '90%'
        //   }
        // },
        plotOptions: this.buildPlotOptions(this.widgetIccidCostDayOfMonth.length),
        series     : series,
        stroke     : {
          curve: 'smooth',
          width: strokeWidths
        },
        title: {
          style: {
            fontWeight: 600,
          },
          text: undefined
        },
        xaxis: {
          type: 'string'
        },
        yaxis: yaxis
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private buildPlotOptions(barCount: number): any {
    try {
      return {
        bar: {
          borderRadius           : 4,
          borderRadiusApplication: 'end',
          columnWidth            : barCount>12 ? '60%' : '40px'
        }
      };
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return { bar: { columnWidth: '60%' } };
    }
  }
  
  public toggleContainerExpansion(): void {
    try {
      this.expandContainer=!this.expandContainer;

      if (this.expandContainer) {
        setTimeout(() => {
          try {
            window.dispatchEvent(new Event('resize'));
          }
          catch(e) {
            this.exceptionsService.handleException(<Error>e);
          }
        }, 0);
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public collapseContainer(): void {
    try {
      this.expandContainer=false;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public closeContainer(): void {
    try {
      this.visibleContainer=false;
      this.containerClosed.emit();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
